/**
 * recurrenceHint — turn a chore template's recurrence JSON into a short
 * descriptor for the card's sub-line ("Daglig", "Hver mandag", "Man, Tir, Fre").
 *
 * The board feed ships the raw `recurrence` string straight off the template
 * (chores.recurrence is a JSON text column validated by the shared Recurrence
 * Zod schema on write). Parsing here keeps the card free of JSON handling and,
 * more importantly, keeps a malformed or future-shaped value from throwing
 * inside a render: anything unrecognised degrades to `{kind: 'none'}` and the
 * card simply shows no hint.
 *
 * Same discipline as homeworkDue.describeDue: describeRecurrence() is
 * i18n-agnostic and returns a `kind` plus its parameters. formatRecurrence()
 * then maps that to Norwegian with an injected `t`, so both the kid-facing
 * ChoreCard and the parent console's template library render repeat text from
 * one implementation instead of two copies of the same switch.
 */
import type { TFunction } from 'i18next';

export type RecurrenceHint =
  | { kind: 'none' }
  | { kind: 'daily' }
  | { kind: 'everyNDays'; interval: number }
  | { kind: 'weekdays'; days: number[] }
  | { kind: 'weekly'; weekday: number }
  | { kind: 'everyNWeeks'; interval: number; weekday: number }
  | { kind: 'monthly'; dayOfMonth: number };

/** ISO weekday (Mon=1 … Sun=7) → i18n key suffix. */
export const WEEKDAY_KEYS = [
  'mon',
  'tue',
  'wed',
  'thu',
  'fri',
  'sat',
  'sun',
] as const;

export type WeekdayKey = (typeof WEEKDAY_KEYS)[number];

/** Map an ISO weekday number to its key, or null when out of range. */
export function weekdayKey(iso: number): WeekdayKey | null {
  return WEEKDAY_KEYS[iso - 1] ?? null;
}

function isIsoWeekday(n: unknown): n is number {
  return typeof n === 'number' && Number.isInteger(n) && n >= 1 && n <= 7;
}

/**
 * Describe a template's recurrence.
 *
 * @param recurrence raw JSON string from the template, or null.
 */
export function describeRecurrence(recurrence: string | null | undefined): RecurrenceHint {
  if (!recurrence) return { kind: 'none' };

  let parsed: unknown;
  try {
    parsed = JSON.parse(recurrence);
  } catch {
    return { kind: 'none' };
  }
  if (typeof parsed !== 'object' || parsed === null) return { kind: 'none' };

  const r = parsed as Record<string, unknown>;
  // 'once' gets no hint on purpose — a one-off chore repeating is the thing
  // worth saying, and "Én gang" on every homework card would be pure noise.
  switch (r.type) {
    case 'daily': {
      const interval = typeof r.interval === 'number' ? r.interval : 1;
      return interval > 1 ? { kind: 'everyNDays', interval } : { kind: 'daily' };
    }
    case 'weekdays': {
      const days = Array.isArray(r.days) ? r.days.filter(isIsoWeekday) : [];
      return days.length > 0 ? { kind: 'weekdays', days: [...days].sort((a, b) => a - b) } : { kind: 'none' };
    }
    case 'weekly': {
      if (!isIsoWeekday(r.weekday)) return { kind: 'none' };
      const interval = typeof r.interval === 'number' ? r.interval : 1;
      return interval > 1
        ? { kind: 'everyNWeeks', interval, weekday: r.weekday }
        : { kind: 'weekly', weekday: r.weekday };
    }
    case 'monthly': {
      const dom = r.dayOfMonth;
      if (typeof dom !== 'number' || !Number.isInteger(dom) || dom < 1 || dom > 31) {
        return { kind: 'none' };
      }
      return { kind: 'monthly', dayOfMonth: dom };
    }
    default:
      return { kind: 'none' };
  }
}

/**
 * Render a recurrence descriptor as Norwegian text, or null when there is
 * nothing worth saying. `t` is injected so this stays a plain function.
 */
export function formatRecurrence(hint: RecurrenceHint, t: TFunction): string | null {
  switch (hint.kind) {
    case 'daily':
      return t('chores.repeat.daily');
    case 'everyNDays':
      return t('chores.repeat.everyNDays', { interval: hint.interval });
    case 'weekly': {
      const key = weekdayKey(hint.weekday);
      return key ? t('chores.repeat.weekly', { day: t(`chores.weekday.${key}`) }) : null;
    }
    case 'everyNWeeks': {
      const key = weekdayKey(hint.weekday);
      return key
        ? t('chores.repeat.everyNWeeks', {
            interval: hint.interval,
            day: t(`chores.weekday.${key}`),
          })
        : null;
    }
    case 'monthly':
      return t('chores.repeat.monthly', { dayOfMonth: hint.dayOfMonth });
    case 'weekdays': {
      const names = hint.days
        .map((d) => weekdayKey(d))
        .filter((k): k is WeekdayKey => k != null)
        .map((k) => t(`chores.weekday.${k}`));
      return names.length > 0 ? t('chores.repeat.weekdays', { days: names.join(', ') }) : null;
    }
    default:
      return null;
  }
}
